import {
  Box,
  Checkbox,
  Flex,
  Grid,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Stack,
  Text,
  useBreakpointValue,
  useOutsideClick,
  Button as CkButton,
} from "@chakra-ui/react";
import Button from "@components/common/Button";
import { handleDefaultRanking } from "@helpers/defaultValues";
import { useVirtualizer } from "@tanstack/react-virtual";
import React from "react";
import { IoCheckmarkCircleOutline, IoClose } from "react-icons/io5";
import { MdArrowBack, MdOutlineWarningAmber } from "react-icons/md";
import { IconLupaError } from "src/assets/IconLupaError";
import { theme } from "src/styles/theme";

export interface OportunidadeBolsao {
  fase: string;
  nomeDaConta: string;
  nomeEmpreendimento: string;
  nomeDaUnidade: string;
  oportunidadeId: string;
  opportunityIdSalesforce: string;
  flagNovoConceito: boolean;
  dataOportunidade: string;
  imobiliariaId: string;
  accountIdSalesforce: string;
  idEmpreendimentoSales: string;
  imobiliaria: string;
  regionalComercial: string;
  contatoCorretor: string;
  ranking: string;
  empreendimentoDeInteresse: string;
  empreendimentoInteresseWeb: string;
  cidadeDeInteresseWeb: string;
  origemConta: string;
  dataUltimaModificacao: string;
  diasSemAtualizacao: number;
  observacoesDiana: string;
  PermitidoQueda: boolean;
  telefonePrincipal: string;
  criadorDaOportunidade: string;
  origemDigital: boolean;
  unidadeNegocio: string;
  dataDePrevisao: string | null;
  motivoReserva: string | null;
  statusAnaliseDeCredito: string | null;
  statusCotacao: string | null;
  dataUltIimaModificacaoSalesforce: string | null;
  tipoBolsao: string;
}

export interface BolsaoDTO {
  tipoBolsao: string;
  quantidadeTransferida: number;
  limiteTotal: number;
  oportunidades: OportunidadeBolsao[];
}

/** Colunas fixas fora do loop para evitar recriação a cada render */
const COL_HEADER = {
  fontSize: "13px",
  fontWeight: "600" as const,
  color: "#374151",
};
const COL_DATA = { fontSize: "13px", color: "#374151" };
const TABLE_COLS_MD = "60px 1fr 120px 160px 1fr 1fr";
const TABLE_COLS_LG = "72px 220px 130px 220px 1fr 1fr";

/** Altura EXATA de cada linha da tabela (px) — deve bater com py={4} = 32px + conteúdo */
const ROW_HEIGHT_DESKTOP = 57;
/** Altura EXATA de cada card mobile (px) */
const ROW_HEIGHT_MOBILE = 148;

export const FilterDropdown: React.FC<{
  unidades: string[];
  unidadeFilter: string[];
  onToggle: (u: string) => void;
  onClose: () => void;
}> = ({ unidades, unidadeFilter, onToggle, onClose }) => {
  const ref = React.useRef<HTMLDivElement>(null);
  useOutsideClick({
    ref: ref as React.RefObject<HTMLElement>,
    handler: onClose,
  });

  return (
    <Box
      ref={ref}
      position="absolute"
      top="calc(100% + 4px)"
      left="0"
      zIndex={9999}
      bg="white"
      boxShadow="0 4px 16px rgba(0,0,0,0.12)"
      borderRadius="10px"
      border="1px solid #E5E7EB"
      p={2}
      minW="160px"
    >
      <Stack spacing={0}>
        {unidades.map((u) => (
          <Flex
            key={u}
            align="center"
            gap="12px"
            px={3}
            py={2}
            borderRadius="6px"
            cursor="pointer"
            _hover={{ bg: "#F3F4F6" }}
            onClick={() => onToggle(u)}
          >
            <Checkbox
              isChecked={unidadeFilter.includes(u)}
              borderColor="#D1D5DB"
              pointerEvents="none"
              w={"fit-content"}
            />
            <Text fontSize="14px" color="#374151">
              {u}
            </Text>
          </Flex>
        ))}
      </Stack>
    </Box>
  );
};

export const RankingBadge: React.FC<{ ranking: string }> = ({ ranking }) => {
  const color = handleDefaultRanking(ranking) ?? "#6B7280";
  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      px="10px"
      py="2px"
      borderRadius="full"
      bg={`${color}22`}
      color={color}
      fontSize="12px"
      fontWeight="500"
      whiteSpace="nowrap"
      minW={"120px"}
      w={"fit-content"}
    >
      {ranking}
    </Box>
  );
};

export const BolsaoTabs: React.FC<{
  bolsoes: { tipoBolsao: string; count: number }[];
  activeTab: string;
  locked?: boolean;
  onChange: (tipoBolsao: string) => void;
  onLockedAttempt?: () => void;
}> = ({ bolsoes, activeTab, locked = false, onChange, onLockedAttempt }) => (
  <Flex borderBottom="1px solid #E5E7EB" mb={2} gap={6} overflowX="auto">
    {bolsoes.map((b) => {
      const isActive = b.tipoBolsao === activeTab;
      const isDisabled = locked && !isActive;

      return (
        <Flex
          key={b.tipoBolsao}
          align="center"
          gap={2}
          pb={3}
          px={1}
          cursor={isDisabled ? "not-allowed" : "pointer"}
          opacity={isDisabled ? 0.5 : 1}
          borderBottom="2px solid"
          borderColor={isActive ? theme.primary : "transparent"}
          w={"fit-content"}
          onClick={() =>
            isDisabled ? onLockedAttempt?.() : onChange(b.tipoBolsao)
          }
        >
          <Text
            fontSize="13px"
            fontWeight={isActive ? "700" : "500"}
            color={isActive ? theme.primary : "#6B7280"}
            whiteSpace="nowrap"
            w={"fit-content"}
          >
            {b.tipoBolsao}
          </Text>
          <Box
            px="6px"
            py="1px"
            borderRadius="full"
            bg={isActive ? "#E3EAFB" : "#E5E7EB"}
            color={isActive ? theme.primary : "#6B7280"}
            fontSize="11px"
            fontWeight="600"
            w={"fit-content"}
          >
            {b.count}
          </Box>
        </Flex>
      );
    })}
  </Flex>
);

export const OportunidadeCard: React.FC<{
  op: OportunidadeBolsao;
  idx: number;
  isSelected: boolean;
  onSelect: (checked: boolean) => void;
}> = React.memo(({ op, idx, isSelected, onSelect }) => (
  <Box
    border="1px solid"
    borderColor={isSelected ? "#1E3A5F" : "#E5E7EB"}
    borderRadius="10px"
    p={4}
    bg={isSelected ? "#F0F4FA" : "white"}
    cursor="pointer"
    onClick={() => onSelect(!isSelected)}
  >
    <Flex align="flex-start" gap={3}>
      <Flex align="center" gap={3} minW={0}>
        <Text w={"fit-content"} fontSize="12px" color="#9CA3AF">
          {idx + 1}
        </Text>
        <Checkbox
          isChecked={isSelected}
          borderColor="#D1D5DB"
          pointerEvents="none"
          w={"fit-content"}
        />
        <Box display={"flex"} minW={0} flexDir={"column"}>
          <Text fontSize="14px" fontWeight="600" color="#111827">
            {op.nomeDaConta}
          </Text>
          <Text fontSize="12px" color="#9CA3AF">
            {op.oportunidadeId}
          </Text>
        </Box>
      </Flex>
    </Flex>
    <Flex p={"4px"}>
      <RankingBadge ranking={op.ranking} />
    </Flex>
    <Grid templateColumns="1fr 1fr 1fr" gap={2} mt={3}>
      <Box>
        <Text fontSize="11px" color="#9CA3AF" mb="2px">
          Unidade de negócio
        </Text>
        <Text fontSize="13px" color="#374151">
          {op.unidadeNegocio}
        </Text>
      </Box>
      <Box>
        <Text fontSize="11px" color="#9CA3AF" mb="2px">
          Empreendimento
        </Text>
        <Text fontSize="13px" color="#374151" noOfLines={1}>
          {op.empreendimentoInteresseWeb}
        </Text>
      </Box>
      <Box>
        <Text fontSize="11px" color="#9CA3AF" mb="2px">
          Cidade
        </Text>
        <Text fontSize="13px" color="#374151" noOfLines={1}>
          {op.cidadeDeInteresseWeb}
        </Text>
      </Box>
    </Grid>
  </Box>
));

export const LimiteAtingido: React.FC<{ onNavigate: () => void }> = ({
  onNavigate,
}) => (
  <Box
    bg="white"
    borderRadius="12px"
    border="1px solid #E5E7EB"
    w="100%"
    display="flex"
    flexDir="column"
    alignItems="center"
    justifyContent="center"
    py={{ base: 10, md: 16 }}
    px={4}
    gap={3}
  >
    <Flex flexDirection="column" w="100%" h="100%" alignItems="center">
      <IconLupaError width="180px" height="180px" />
      <Text
        fontWeight="600"
        fontSize={{ base: "15px", md: "16px" }}
        color="#111827"
        textAlign="center"
        mt={2}
      >
        Você atingiu seu limite de transferências hoje
      </Text>
      <Text
        fontSize="13px"
        color="#6B7280"
        textAlign="center"
        maxW="420px"
        mt={1}
      >
        Volte amanhã para consultar e transferir mais oportunidades do bolsão
      </Text>
      <Box mt={4} w={{ base: "100%", sm: "260px" }}>
        <Button
          title={
            <Flex align="center" justify="center" gap="8px">
              <Box w={"fit-content"}>
                <MdArrowBack />
              </Box>
              <Text w={"fit-content"}>Ir para minhas oportunidades</Text>
            </Flex>
          }
          onClick={onNavigate}
        />
      </Box>
    </Flex>
  </Box>
);

export const SuccessModal: React.FC<{
  isOpen: boolean;
  count: number;
  hasWarning?: boolean;
  message?: string;
  onClose: () => void;
  onNavigate: () => void;
}> = ({ isOpen, count, hasWarning = false, message, onClose, onNavigate }) => {
  const bgColor = hasWarning ? "#FFFBEB" : "white";
  const borderColor = hasWarning ? "#FCD34D" : "#E5E7EB";
  const iconColor = hasWarning ? "#D97706" : theme.primary;

  const formatWarningMessage = (msg: string) => {
    const [firstPart, secondPart] = msg.split(/\.\s+(?=Ja as seguintes)/);

    const boldify = (text: string) =>
      text.split(/"([^"]+)"/g).map((part, i) =>
        i % 2 === 1 ? (
          <Text as="strong" key={i}>
            {part}
          </Text>
        ) : (
          part
        ),
      );

    return (
      <Flex flexDir="column" gap={3} textAlign="center">
        <Text fontSize="13px" color={theme.text50}>
          {boldify(firstPart)}
        </Text>
        {secondPart && (
          <Text fontSize="13px" color={theme.text50} fontWeight="500">
            {boldify(secondPart)}
          </Text>
        )}
      </Flex>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="sm">
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(2px)" />
      <ModalContent
        borderRadius="16px"
        p={4}
        mx={4}
        bg={bgColor}
        border={`1px solid #E5E7EB`}
      >
        <ModalBody p={0}>
          <Flex flexDir="column" align="center" gap={4}>
            <Flex w="100%" justify="flex-end">
              <IconButton
                aria-label="Fechar"
                icon={<IoClose size={20} />}
                variant="ghost"
                size="sm"
                onClick={onClose}
                color="#6B7280"
                w="fit-content"
              />
            </Flex>

            <Box
              w="56px"
              h="56px"
              borderRadius="full"
              border={`2px solid ${iconColor}`}
              display="flex"
              alignItems="center"
              justifyContent="center"
              color={iconColor}
              fontSize="28px"
            >
              {hasWarning ? (
                <MdOutlineWarningAmber />
              ) : (
                <IoCheckmarkCircleOutline />
              )}
            </Box>

            <Text
              fontWeight="600"
              fontSize="17px"
              color="#111827"
              textAlign="center"
            >
              {hasWarning
                ? "Transferência parcialmente concluída"
                : "Transferência concluída com sucesso"}
            </Text>

            <Text fontSize="13px" color="#6B7280" textAlign="center">
              {hasWarning && message ? (
                formatWarningMessage(message)
              ) : (
                <Text fontSize="13px" color="#6B7280" textAlign="center">
                  Você transferiu <strong>{count}</strong>{" "}
                  {count === 1 ? "oportunidade" : "oportunidades"} do bolsão
                  para <strong>"Minhas Oportunidades"</strong>
                </Text>
              )}
            </Text>

            <Flex flexDir="column" w="100%" gap={2} mt={2}>
              <CkButton
                onClick={onNavigate}
                _hover={{ opacity: 1 }}
                border={"none"}
                h="42px"
                borderRadius="8px"
                fontSize="14px"
                alignItems="center"
                justifyContent="center"
                backgroundColor={iconColor}
                color={"white"}
                opacity={0.8}
              >
                Ver minhas oportunidades
              </CkButton>

              <CkButton
                onClick={onClose}
                _hover={{ opacity: 1 }}
                border={`2px solid ${iconColor}`}
                h="42px"
                borderRadius="8px"
                fontSize="14px"
                alignItems="center"
                justifyContent="center"
                backgroundColor={"white"}
                color={iconColor}
                opacity={0.8}
              >
                Voltar ao bolsão
              </CkButton>
            </Flex>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

// ─────────────────────────────────────────────
// Componente de paginação
// ─────────────────────────────────────────────
export const Pagination: React.FC<{
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}> = ({ currentPage, totalPages, totalItems, pageSize, onPageChange }) => {
  if (totalPages <= 1) return null;

  const start = (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, totalItems);

  const getPages = (): (number | "...")[] => {
    if (totalPages <= 7)
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (currentPage <= 4) return [1, 2, 3, 4, 5, "...", totalPages];
    if (currentPage >= totalPages - 3)
      return [
        1,
        "...",
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    return [
      1,
      "...",
      currentPage - 1,
      currentPage,
      currentPage + 1,
      "...",
      totalPages,
    ];
  };

  return (
    <Flex
      align="center"
      justify="space-between"
      px={{ base: 3, md: 6 }}
      py={2}
      borderTop="1px solid #E5E7EB"
      flexWrap="wrap"
      gap={2}
    >
      <Text fontSize="12px" color="#6B7280">
        Mostrando{" "}
        <Text as="span" fontWeight="600" color="#374151">
          {start}–{end}
        </Text>{" "}
        de{" "}
        <Text as="span" fontWeight="600" color="#374151">
          {totalItems}
        </Text>{" "}
        oportunidades
      </Text>

      <Flex align="center" gap={1}>
        <IconButton
          aria-label="Página anterior"
          icon={<MdArrowBack />}
          size="sm"
          variant="ghost"
          isDisabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
          color="#374151"
          w={"fit-content"}
        />

        {getPages().map((p, i) =>
          p === "..." ? (
            <Text
              key={`ellipsis-${i}`}
              fontSize="13px"
              color="#9CA3AF"
              px={1}
              w={"fit-content"}
            >
              …
            </Text>
          ) : (
            <Box
              key={p}
              w="32px"
              h="32px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              borderRadius="6px"
              cursor="pointer"
              fontSize="13px"
              fontWeight={currentPage === p ? "700" : "400"}
              color={currentPage === p ? "white" : "#374151"}
              bg={currentPage === p ? "#1E3A5F" : "transparent"}
              _hover={{ bg: currentPage === p ? "#1E3A5F" : "#F3F4F6" }}
              transition="all 0.1s"
              onClick={() => onPageChange(p as number)}
            >
              {p}
            </Box>
          ),
        )}

        <IconButton
          aria-label="Próxima página"
          icon={<MdArrowBack style={{ transform: "rotate(180deg)" }} />}
          size="sm"
          variant="ghost"
          isDisabled={currentPage === totalPages}
          onClick={() => onPageChange(currentPage + 1)}
          color="#374151"
          w={"fit-content"}
        />
      </Flex>
    </Flex>
  );
};

// ─────────────────────────────────────────────
// Linha da tabela — memo para evitar re-render fora do viewport
// ─────────────────────────────────────────────
export const TableRow: React.FC<{
  op: OportunidadeBolsao;
  absoluteIdx: number;
  isSelected: boolean;
  onSelectOne: (id: string, checked: boolean) => void;
  style: React.CSSProperties;
  templateColumns: string;
  columnGap: number;
  px: number;
}> = React.memo(
  ({
    op,
    absoluteIdx,
    isSelected,
    onSelectOne,
    style,
    templateColumns,
    columnGap,
    px,
  }) => (
    <Grid
      templateColumns={templateColumns}
      alignItems="center"
      columnGap={columnGap}
      px={px}
      py={4}
      borderBottom="1px solid #F3F4F6"
      minW="640px"
      cursor="default"
      position="absolute"
      top={0}
      left={0}
      right={0}
      style={style}
    >
      <Flex align="center" gap={2} justify="center" minW="60px" ml={"-20px"}>
        <Text
          fontSize="12px"
          color="#9CA3AF"
          w="32px"
          textAlign="right"
          flexShrink={0}
        >
          {absoluteIdx + 1}
        </Text>
        <Checkbox
          isChecked={isSelected}
          onChange={(e) => onSelectOne(op.oportunidadeId, e.target.checked)}
          borderColor="#D1D5DB"
          cursor="pointer"
          sx={{ "& .chakra-checkbox__control": { opacity: "1 !important" } }}
        />
      </Flex>

      <Box minW={0}>
        <Text fontSize="13px" fontWeight="600" color="#111827" noOfLines={1}>
          {op.nomeDaConta}
        </Text>
        <Text fontSize="12px" color="#9CA3AF">
          {op.oportunidadeId}
        </Text>
      </Box>

      <Box>
        <RankingBadge ranking={op.ranking} />
      </Box>

      <Text {...COL_DATA} textAlign="center" ml="-18px">
        {op.unidadeNegocio}
      </Text>

      <Text {...COL_DATA} noOfLines={1}>
        {op.empreendimentoInteresseWeb}
      </Text>

      <Text {...COL_DATA} noOfLines={1}>
        {op.cidadeDeInteresseWeb}
      </Text>
    </Grid>
  ),
);

// ─────────────────────────────────────────────
// Tabela desktop virtualizada
// ─────────────────────────────────────────────
export const VirtualizedTable: React.FC<{
  oportunidades: OportunidadeBolsao[];
  selectedIds: Set<string>;
  allSelected: boolean;
  selectedCount: number;
  onSelectOne: (id: string, checked: boolean) => void;
  onSelectAll: (checked: boolean) => void;
  unidades: string[];
  unidadeFilter: string[];
  toggleUnidade: (u: string) => void;
  isFilterOpen: boolean;
  setIsFilterOpen: (v: boolean) => void;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  pageSize: number;
}> = ({
  oportunidades,
  selectedIds,
  allSelected,
  selectedCount,
  onSelectOne,
  onSelectAll,
  unidades,
  unidadeFilter,
  toggleUnidade,
  isFilterOpen,
  setIsFilterOpen,
  currentPage,
  totalPages,
  totalItems,
  onPageChange,
  pageSize,
}) => {
  const parentRef = React.useRef<HTMLDivElement>(null);

  const templateColumns =
    useBreakpointValue({
      base: TABLE_COLS_MD,
      md: TABLE_COLS_MD,
      lg: TABLE_COLS_LG,
    }) ?? TABLE_COLS_LG;
  const columnGap = useBreakpointValue({ base: 3, md: 3, lg: 4 }) ?? 4;
  const px = useBreakpointValue({ base: 4, md: 4, lg: 6 }) ?? 6;

  const rowVirtualizer = useVirtualizer({
    count: oportunidades.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT_DESKTOP,
    overscan: 10,
  });

  return (
    <Box
      bg="white"
      borderRadius="12px"
      border="1px solid #E5E7EB"
      w="100%"
      h={{ base: "100%", md: "430px", "2xl": "80vh" }}
      overflow="hidden"
      display="flex"
      flexDir="column"
    >
      {/* Cabeçalho fixo — fora do scroll */}
      <Grid
        templateColumns={templateColumns}
        alignItems="center"
        columnGap={columnGap}
        px={px}
        py="14px"
        borderBottom="1px solid #E5E7EB"
        minW="640px"
        flexShrink={0}
      >
        <Flex align="center" justify="center" gap={2} ml={"-8px"}>
          <Checkbox
            w="fit-content"
            isChecked={allSelected}
            isIndeterminate={selectedCount > 0 && !allSelected}
            onChange={() => onSelectAll(selectedCount > 0 ? false : true)}
            borderColor="#D1D5DB"
          />
        </Flex>
        <Text {...COL_HEADER}>Oportunidade</Text>
        <Text {...COL_HEADER}>Ranking</Text>

        {/* Filtro de Unidade de negócio */}
        <Box position="relative">
          <Flex align="center" justify="center" gap={1} ml={"-12px"}>
            <Text {...COL_HEADER} whiteSpace="nowrap" w="fit-content">
              Unidade de negócio
            </Text>
          </Flex>
        </Box>

        <Text {...COL_HEADER}>Empreendimento de interesse web</Text>
        {/* <Text {...COL_HEADER}>Emp. de interesse web</Text> */}

        <Text {...COL_HEADER}>Cidade de interesse web</Text>
      </Grid>

      {/* Área virtualizada */}
      <Box
        ref={parentRef}
        overflowY="auto"
        overflowX="auto"
        h={{ base: "100%", md: "440px", "2xl": "80vh" }}
        position="relative"
        style={{ willChange: "transform" }}
        css={{
          "&::-webkit-scrollbar": { width: "6px", height: "6px" },
          "&::-webkit-scrollbar-track": { background: "#F9FAFB" },
          "&::-webkit-scrollbar-thumb": {
            background: "#D1D5DB",
            borderRadius: "3px",
          },
        }}
      >
        {oportunidades.length === 0 ? (
          <Flex h="100%" align="center" justify="center">
            <Text fontSize="13px" color="#9CA3AF" w={"fit-content"}>
              Não há oportunidades para transferência.
            </Text>
          </Flex>
        ) : (
          <Box
            position="relative"
            h={`${rowVirtualizer.getTotalSize()}px`}
            minW="640px"
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const op = oportunidades[virtualRow.index];
              const absoluteIdx =
                (currentPage - 1) * pageSize + virtualRow.index;

              return (
                <TableRow
                  key={op.oportunidadeId}
                  op={op}
                  absoluteIdx={absoluteIdx}
                  isSelected={selectedIds.has(op.oportunidadeId)}
                  onSelectOne={onSelectOne}
                  templateColumns={templateColumns}
                  columnGap={columnGap}
                  px={px}
                  style={{ transform: `translateY(${virtualRow.start}px)` }}
                />
              );
            })}
          </Box>
        )}
      </Box>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={onPageChange}
      />
    </Box>
  );
};

// ─────────────────────────────────────────────
// Lista mobile virtualizada
// ─────────────────────────────────────────────
export const VirtualizedMobileList: React.FC<{
  oportunidades: OportunidadeBolsao[];
  selectedIds: Set<string>;
  selectedCount: number;
  allSelected: boolean;
  onSelectOne: (id: string, checked: boolean) => void;
  onSelectAll: (checked: boolean) => void;
  unidades: string[];
  unidadeFilter: string[];
  toggleUnidade: (u: string) => void;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  setIsFilterOpen: (v: boolean) => void;
  isFilterOpen: boolean;
  pageSize: number;
}> = ({
  oportunidades,
  selectedIds,
  selectedCount,
  allSelected,
  onSelectOne,
  onSelectAll,
  unidades,
  unidadeFilter,
  toggleUnidade,
  currentPage,
  totalPages,
  totalItems,
  onPageChange,
  setIsFilterOpen,
  isFilterOpen,
  pageSize,
}) => {
  const parentRef = React.useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: oportunidades.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT_MOBILE,
    overscan: 5,
    measureElement: (el) => el.getBoundingClientRect().height,
  });

  return (
    <Box display="flex" flexDir="column" gap={3}>
      {/* Barra de filtro mobile */}
      <Flex justify="space-between" align="center">
        <Text fontSize="13px" color="#6B7280">
          {totalItems} oportunidades
          {unidadeFilter.length > 0 && (
            <Text as="span" color="#1E3A5F" fontWeight="600">
              {" "}
              · filtrado
            </Text>
          )}
        </Text>
        <Flex align={"center"} gap={2} w={"fit-content"}>
          {selectedCount > 0 && (
            <Text w={"100px"} fontSize="12px" color="#1E3A5F" fontWeight="600">
              {selectedCount} selecionado(s)
            </Text>
          )}
          {/* <Box position="relative" w={"fit-content"}>
            <IconButton
              aria-label="Filtrar unidade"
              icon={<MdOutlineFilterList />}
              size="sm"
              variant="ghost"
              color={unidadeFilter.length > 0 ? "#1E3A5F" : "#9CA3AF"}
              onClick={() => setIsFilterOpen(!isFilterOpen)}
            />
            {isFilterOpen && (
              <FilterDropdown
                unidades={unidades}
                unidadeFilter={unidadeFilter}
                onToggle={toggleUnidade}
                onClose={() => setIsFilterOpen(false)}
              />
            )}
          </Box> */}
          <Checkbox
            isChecked={allSelected}
            isIndeterminate={selectedCount > 0 && !allSelected}
            onChange={() => onSelectAll(selectedCount > 0 ? false : true)}
            borderColor="#D1D5DB"
            aria-label="Selecionar todos"
            w={"fit-content"}
          />
        </Flex>
      </Flex>

      {/* Container virtualizado mobile */}
      <Box
        ref={parentRef}
        h="60vh"
        overflowY="auto"
        position="relative"
        style={{ willChange: "transform" }}
        css={{
          "&::-webkit-scrollbar": { width: "4px" },
          "&::-webkit-scrollbar-thumb": {
            background: "#D1D5DB",
            borderRadius: "2px",
          },
        }}
      >
        <Box position="relative" h={`${rowVirtualizer.getTotalSize()}px`}>
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const op = oportunidades[virtualRow.index];
            const absoluteIdx = (currentPage - 1) * pageSize + virtualRow.index;

            return (
              <Box
                key={op.oportunidadeId}
                data-index={virtualRow.index}
                ref={rowVirtualizer.measureElement}
                position="absolute"
                top={0}
                left={0}
                right={0}
                style={{ transform: `translateY(${virtualRow.start}px)` }}
                pb={3}
              >
                <OportunidadeCard
                  op={op}
                  idx={absoluteIdx}
                  isSelected={selectedIds.has(op.oportunidadeId)}
                  onSelect={(checked) =>
                    onSelectOne(op.oportunidadeId, checked)
                  }
                />
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Paginação mobile */}
      <Box
        bg="white"
        borderRadius="10px"
        border="1px solid #E5E7EB"
        overflow="hidden"
      >
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
          onPageChange={onPageChange}
        />
      </Box>
    </Box>
  );
};
